import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { InboxList, ghostLinkStyle, localizedInboxText, type InboxRow } from '@kilat/ui';
import { formatRelativeTime } from '@kilat/shared';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { useAgentInbox } from '../lib/push/agent-push.js';
import { markInboxRead } from '../lib/repositories/inbox-repo.js';

/**
 * /inbox — the agent's notifications: milestones, sign-ups, commissions,
 * payouts. The server had been writing milestone messages to
 * `users/{agentUid}/inbox` with no screen to read them; this is that screen.
 *
 * Tap → mark read + open the in-app deep link. "Mark all as read" clears the
 * header bell in one batched write. Loading / error-with-retry / empty states
 * come from the shared InboxList.
 */
export function InboxPage() {
  const t = useI18n();
  const nav = useNavigate();
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const onError = useCallback((err: Error) => setLoadError(err), []);
  const { uid, messages, loading } = useAgentInbox(onError, retryNonce);

  function open(id: string) {
    const m = messages.find((x) => x.id === id);
    if (!m) return;
    if (!m.readAt && uid) {
      void markInboxRead(uid, [m.id]).catch((e) =>
        logger.warn('inbox mark-read failed', { id: m.id, error: String(e) }),
      );
    }
    if (m.deepLink && m.deepLink.startsWith('/')) nav(m.deepLink);
  }

  async function markAll(): Promise<void> {
    if (!uid) return;
    const ids = messages.filter((m) => !m.readAt).map((m) => m.id);
    try {
      await markInboxRead(uid, ids);
    } catch (e) {
      logger.warn('inbox mark-all-read failed', { count: ids.length, error: String(e) });
      throw e;
    }
  }

  const nowMs = Date.now();
  const rows: InboxRow[] = messages.map((m) => {
    const text = localizedInboxText(m, t.locale);
    return {
      id: m.id,
      title: text.title,
      body: text.body,
      when: formatRelativeTime(tsMs(m.createdAt), nowMs, t.locale),
      unread: !m.readAt,
      kindLabel:
        m.kind === 'transactional'
          ? { text: t.t('common.inbox.kindUpdate'), tone: 'info' }
          : m.kind === 'milestone'
            ? { text: t.t('common.inbox.kindMilestone'), tone: 'success' }
            : { text: t.t('common.inbox.kindNote'), tone: 'neutral' },
    };
  });

  return (
    <main
      className="ag-page"
      style={{
        minHeight: '100dvh',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      <header>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>
          {t.t('agent.inbox.title')}
        </h1>
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t.t('agent.inbox.subtitle')}
        </p>
      </header>
      <InboxList
        rows={rows}
        loading={loading && !loadError}
        error={Boolean(loadError)}
        onRetry={() => {
          setLoadError(null);
          setRetryNonce((n) => n + 1);
        }}
        onOpen={open}
        onMarkAllRead={markAll}
        emptyAction={
          <Link to="/" style={ghostLinkStyle}>
            {t.t('agent.inbox.emptyAction')}
          </Link>
        }
        copy={{
          errorTitle: t.t('agent.inbox.errorTitle'),
          errorBody: t.t('agent.inbox.errorBody'),
          retry: t.t('common.tryAgain'),
          emptyTitle: t.t('agent.inbox.emptyTitle'),
          emptyBody: t.t('agent.inbox.emptyBody'),
          markAllRead: t.t('common.inbox.markAllRead'),
          markingAllRead: t.t('common.inbox.markingAllRead'),
          markAllFailed: t.t('common.inbox.markAllFailed'),
          unreadSummary: (count) => t.t('common.inbox.unreadSummary', { count }),
          allRead: t.t('common.inbox.allRead'),
          openAria: (title) => t.t('agent.inbox.openAria', { title }),
        }}
      />
    </main>
  );
}

/** Firestore Timestamp → epoch ms, tolerant of bare `{ seconds }` fixtures. */
function tsMs(ts: { toMillis?: () => number; seconds: number }): number {
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}
